import type { PostHogConfig } from '@posthog/types';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { SnackbarProvider } from 'notistack';
import { PostHogErrorBoundary, PostHogProvider } from 'posthog-js/react';
import React, { useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate, PersistGateProps } from 'redux-persist/integration/react';
import { StreamChat } from 'stream-chat';
import { Chat } from 'stream-chat-react';

import {
  ContextBarProvider,
  CustomLabels,
  FlagsContext,
  PortalConfig,
  PortalConfigContext,
  RouteContext,
  SessionData,
} from 'src/context';
import { FontProvider } from 'src/context/FontProvider';
import { ElectronIntegration } from 'src/features/electron';
import { useIsAssemblyBrandEnabled } from 'src/hooks/useIsAssemblyBrandEnabled';
import { initI18nResources } from 'src/i18n/i18next.config';
import { ActivitySessionTracker } from 'src/legacy/components/Analytics/ActivitySessionTracker';
import { AnalyticsTracker } from 'src/legacy/components/Analytics/Tracker';
import { ConfigureAuth } from 'src/legacy/components/Auth/ConfigureAuth';
import { ChoosePlanAlertDialog } from 'src/legacy/components/ChoosePlanAlertDialog';
import { ChurnAccessModal } from 'src/legacy/components/ChoosePlanAlertDialog/ChurnAccessModal';
import Notifier from 'src/legacy/components/Notifier/Notifier';
import { AdditionalWorkspaceUpgradeModal } from 'src/routes/settings/plans/components/AdditionalWorkspaceUpgradeModal';
import { chatSocketLogger } from 'src/store/messages/chatSocketLog';
import { persistAdClickIds } from 'src/utils/adClickIds';
import { scopeChatClientToStack } from 'src/utils/chatStackScope';
import { buildBrowserHostGate } from 'src/utils/posthogHostGate';

import { AppErrorFallback } from './AppErrorFallback';
import { AppProviders } from './AppProviders';
import { WithUserInitialization } from './WithUserInitialization';

interface AppProps {
  context: {
    pathname: string;
    query: Record<string, any>;
    isMobile: boolean;
    store: any;
    stripeContext: { publishableKey: string };
    portalConfig: any;
    flags: any;
    posthogBootstrap: {
      featureFlags: Record<string, boolean | string>;
    };
    sessionData: SessionData;
    title?: string;
    description?: string;
    flexibleAuth: boolean;
    params: Record<string, string>;
    posthogConfig: {
      apiKey: string;
      host: string;
    };
  };
  children: React.ReactNode;
}
/**
 * The top-level React component setting context (global) variables
 * that can be accessed from all the child components.
 *
 * https://facebook.github.io/react/docs/context.html
 *
 * Usage example:
 *
 *   const context = {
 *     history: createBrowserHistory(),
 *     store: createStore(),
 *   };
 *
 *   ReactDOM.render(
 *     <App context={context}>
 *       <PageShell>
 *         <LandingPage />
 *       </PageShell>
 *     </App>,
 *     container,
 *   );
 */

/**
 * This is the persister gate provider wrapper.
 * When the persistor is not used then it will return the children.
 * Otherwise, it wraps the apps children with the persistor gate.
 * The persistor gate is used to rehydrate the redux store from the local storage.
 * It shows a loading page while the store is being rehydrated.
 */
const PersistGateProvider: React.FC<{
  children?: React.ReactNode;
  // the persistor can be undefined when the web app do not use persistency
  // e.g. unauth experience, or the app component is rendered in the server side.
  persistor?: PersistGateProps['persistor'] | undefined;
}> = ({ persistor, children }) => {
  if (!persistor) {
    return <>{children}</>;
  }
  return <PersistGate persistor={persistor}>{children}</PersistGate>;
};
const App: React.FC<AppProps> = ({ context, children }) => {
  // NOTE: If you need to add or modify header, footer etc. of the app,
  // please do that inside the layout shell (e.g. PageShell).
  const {
    pathname,
    query,
    isMobile,
    store,
    stripeContext,
    portalConfig,
    flags,
    posthogBootstrap,
    sessionData,
    title,
    description,
    flexibleAuth,
    params,
    posthogConfig,
  } = context;

  const routeContext = React.useMemo(
    () => ({
      pathname,
      query,
      isMobile,
      sessionData,
      title,
      description,
      params,
    }),
    [pathname, query, isMobile, sessionData, title, description, params],
  );

  const [appConfigurations, setAppConfigurations] =
    React.useState<PortalConfig>(portalConfig);

  // Ad click ids live on the landing URL. Later signup steps rebuild that URL
  // without them, so copy msclkid and twclid onto cookies before children navigate.
  persistAdClickIds();

  // Navigation resolves outside React and must see terminology edits from this tab.
  useEffect(() => {
    context.portalConfig = appConfigurations;
  }, [context, appConfigurations]);

  const { isAssemblyBrandEnabled } = useIsAssemblyBrandEnabled();

  useEffect(() => {
    initI18nResources({
      customLabels: appConfigurations.features.customLabels,
      isAssembly: isAssemblyBrandEnabled,
    });
  }, [appConfigurations.features.customLabels, isAssemblyBrandEnabled]);

  const [stripePromise] = React.useState(
    loadStripe(stripeContext.publishableKey, {
      developerTools: { assistant: { enabled: false } },
    }).catch(() => {
      return null;
    }),
  );

  const chatClient = React.useMemo(() => {
    const client = StreamChat.getInstance(portalConfig.Chat.ClientId, {
      timeout: 10000,
      baseURL: 'https://chat.stream-io-api.com',
      logger: chatSocketLogger,
    });
    return scopeChatClientToStack(client, portalConfig.Chat.StackId);
  }, []);

  // Feature flags state - starts with server-bootstrapped flags, syncs when PostHog is ready

  const { featureFlags } = posthogBootstrap ?? {};

  const posthogOptions = React.useMemo(() => {
    return {
      api_host: posthogConfig.host,
      ui_host: 'https://us.posthog.com',
      defaults: '2025-11-30' as const,
      // Bootstrap flags (never identity) from the server to avoid a
      // client-side /flags API call; posthog-js owns the distinct ID.
      bootstrap: {
        featureFlags: featureFlags,
      },

      // Disable fetching flags on first load since we bootstrap from server
      advanced_disable_feature_flags_on_first_load: true,
      capture_exceptions: true,
      // Flags still fetch and evaluate on a dev machine; only the outbound events are dropped.
      before_send: buildBrowserHostGate(),
    } satisfies Partial<PostHogConfig>;
  }, [posthogConfig, featureFlags]);
  /**
   *
   * @param config {PortalConfig}
   * @description updates the portal configurations
   */
  const updateAppConfigurations = (config: {
    id: string;
    sendEmailsToUninvitedClients: boolean;
    clientExperienceEnabled: boolean;
    clientInvitesDisabled: boolean;
    disableClientEmailNotifications: boolean;
    disableCompanies: boolean;
    disableGoogleSignIn: boolean;
    gaMeasurementId: string;
    signupDisabled: boolean;
    signoutRedirectUrl: string;
    authPreferences: {
      enableMagicLinks: boolean;
    };
    customLabels: CustomLabels;
  }) => {
    setAppConfigurations((previous) => ({
      ...previous,
      id: config.id,
      clientExperienceEnabled: config.clientExperienceEnabled,
      disableClientEmailNotifications: config.disableClientEmailNotifications,
      sendEmailsToUninvitedClients: config.sendEmailsToUninvitedClients,
      metadata: {
        ...previous.metadata,
        gaMeasurementId: config.gaMeasurementId,
      },
      SignoutRedirectURL: config.signoutRedirectUrl,
      features: {
        ...previous.features,
        disableCompanies: config.disableCompanies,
        disableGoogleSignIn: config.disableGoogleSignIn,
        signupDisabled: config.signupDisabled,
        clientInvitesDisabled: config.clientInvitesDisabled,
        customLabels: {
          ...previous.features.customLabels,
          ...config.customLabels,
        },
      },
      authPreferences: config.authPreferences,
    }));
  };

  const configurations = React.useMemo(
    () => ({
      ...appConfigurations,
      updatePortalConfig: updateAppConfigurations,
    }),
    [appConfigurations],
  );

  return (
    <PostHogProvider apiKey={posthogConfig.apiKey} options={posthogOptions}>
      <PostHogErrorBoundary
        fallback={
          <AppErrorFallback isInternal={portalConfig.viewMode === 'internal'} />
        }
      >
        <PortalConfigContext.Provider value={configurations}>
          <AppProviders>
            <ReduxProvider store={store}>
              <FontProvider />
              <PersistGateProvider persistor={store.persistor}>
                <Elements stripe={stripePromise}>
                  <Chat client={chatClient} theme="portal-chat">
                    <FlagsContext.Provider value={flags}>
                      <ContextBarProvider>
                        <RouteContext.Provider value={routeContext}>
                          <AnalyticsTracker />
                          <ActivitySessionTracker />
                          <ChoosePlanAlertDialog />
                          <ChurnAccessModal />
                          <AdditionalWorkspaceUpgradeModal />
                          <SnackbarProvider
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            maxSnack={2}
                          >
                            <ElectronIntegration />
                            <Notifier />
                            <ConfigureAuth />
                            <WithUserInitialization flexibleAuth={flexibleAuth}>
                              {children}
                            </WithUserInitialization>
                          </SnackbarProvider>
                        </RouteContext.Provider>
                      </ContextBarProvider>
                    </FlagsContext.Provider>
                  </Chat>
                </Elements>
              </PersistGateProvider>
            </ReduxProvider>
          </AppProviders>
        </PortalConfigContext.Provider>
      </PostHogErrorBoundary>
    </PostHogProvider>
  );
};

export default App;
